import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { FormProvider, useForm } from "react-hook-form";
import { useReactToPrint } from "react-to-print";

import ProductionScriptBlockInfo from "./ProductionScriptBlockInfo";
import ProductionWysiwyg from "./ProductionWysiwyg";
import ScenesBlock from "./ScenesBlock";
import ScriptSelection from "./ScriptSelection";
import Text from "@/components/Text";
import ToggableHeading from "@/components/ToggableHeading";
import Notes, { Li } from "@/components/Testing/Notes";
import ScriptCreationButtons from "./ScriptCreationButtons";
import {
  ProductionProjectResource,
  ProductionSceneActorResource,
  ProductionSceneLocationResource,
  ProductionSceneNoteResource,
  ProductionScenePropResource,
  ProductionSceneShoppingItemResource,
} from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 32px;
  margin-bottom: 54px;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
`;

const Left = styled.div<{ $showSidebar: boolean }>`
  width: 30%;
  transition: margin 0.2s ease-out, opacity 0.7s ease-in;
  opacity: 1;
  margin-left: 0;
  ${({ $showSidebar }) =>
    !$showSidebar &&
    css`
      margin-left: -30.1%;
      opacity: 0;
    `}
`;

const Right = styled.div`
  width: 70%;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

const BoxInner = styled.div<{ $showSidebar: boolean }>`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  padding: 48px 0;
  ${({ $showSidebar }) =>
    !$showSidebar &&
    css`
      overflow: hidden;
    `}
`;

const StyledNotes = styled(Notes)`
  margin-bottom: 24px;
`;

export interface SceneFormT {
  name: string;
  content: string;
}

interface Props {
  project: ProductionProjectResource;
  activeScriptId: number | null;
  setActiveScriptId: React.Dispatch<React.SetStateAction<number | null>>;
  sidebarData:
    | {
        shoppingItems: ProductionSceneShoppingItemResource[][];
        props: ProductionScenePropResource[][];
        locations: ProductionSceneLocationResource[][];
        actors: ProductionSceneActorResource[][];
        notes: ProductionSceneNoteResource[][];
        scenes: number[];
      }
    | null
    | undefined;
}

const ProductionScriptBlock: FC<Props> = ({
  activeScriptId,
  project,
  setActiveScriptId,
  sidebarData,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const [showSidebar, setShowSidebar] = useState(true);
  const [commentsEnabled, setCommentsEnabled] = useState(true);

  const alerts = useAlerts();

  const activeScript = useMemo(() => {
    if (!activeScriptId) return null;

    return project.scripts.find((s) => s.id === activeScriptId) ?? null;
  }, [activeScriptId, project]);

  const [activeSceneId, setActiveSceneId] = useState<number | null>(null);

  const sceneRef = useRef<HTMLDivElement>(null);

  const print = useReactToPrint({
    content: () => sceneRef.current,
  });

  const { activeScene, activeSceneNumber } = useMemo(() => {
    const sceneIndex =
      activeScript?.scenes?.findIndex((s) => s.id === activeSceneId) ?? -1;

    return {
      activeScene:
        activeScript?.scenes?.[sceneIndex] ?? activeScript?.scenes?.[0],
      activeSceneNumber: sceneIndex + 1,
    };
  }, [activeScript?.scenes, activeSceneId]);

  useEffect(() => {
    setActiveSceneId(activeScript?.scenes?.[0]?.id ?? null);
    // TODO: find better way
    // eslint-disable-next-line
  }, [activeScript?.id]);

  const methods = useForm<SceneFormT>({
    defaultValues: {
      name: "",
      content: "",
    },
  });

  useEffect(() => {
    methods.reset({
      name: activeScene?.name ?? "",
      content: activeScene?.content ?? "",
    });
  }, [activeScene, methods]);

  const setFirstSceneAsActive = useCallback(() => {
    setActiveSceneId(activeScript?.scenes?.[0]?.id ?? null);
  }, [activeScript]);

  const ensureFormIsNotDirty = useCallback(
    (callback: () => void) => {
      if (methods.formState.isDirty) {
        alerts
          .confirm(
            "You have unsaved changes. Are you sure you would like to proceed?",
            "Unsaved changes will be lost"
          )
          .then((confirmed) => {
            alerts.close();
            confirmed && callback();
          });
        return;
      }
      callback();
    },
    [methods.formState.isDirty, alerts]
  );

  return (
    <>
      <ToggableHeading
        heading="SCRIPT"
        onToggle={() => setIsOpen((prev) => !prev)}
        isOpen={isOpen}
      />
      {isOpen && (
        <>
          <StyledNotes heading="Script notes">
            <ul>
              <Li>
                <Text $type="smallMedium">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Massa
                  phasellus libero diam quis tortor placerat id tempor.
                </Text>
              </Li>
              <Li>
                <Text $type="smallMedium">
                  Velit viverra feugiat feugiat quam nisi. Nulla est velit
                  ultrices cursus sit. Lorem ipsum dolor sit amet, consectetur
                  adipiscing elit.
                </Text>
              </Li>
              <Li>
                <Text $type="smallMedium">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nec
                  dui ac molestie tortor nullam non risus, adipiscing. Ornare.
                </Text>
              </Li>
            </ul>
          </StyledNotes>
          {!!project.scripts?.length ? (
            <>
              {!!activeScript && (
                <>
                  <ScriptSelection
                    activeScriptId={activeScriptId}
                    setActiveScriptId={(scriptId: number | null) =>
                      ensureFormIsNotDirty(() => setActiveScriptId(scriptId))
                    }
                    scripts={project.scripts}
                  />
                  {!!activeScene && (
                    <FormProvider {...methods}>
                      <ScenesBlock
                        print={print}
                        scenes={activeScript!.scenes!}
                        scriptId={activeScriptId!}
                        activeScene={activeScene}
                        setActiveSceneId={(
                          sceneId: number | null,
                          ignoreCheck = false
                        ) =>
                          ignoreCheck
                            ? setActiveSceneId(sceneId)
                            : ensureFormIsNotDirty(() =>
                                setActiveSceneId(sceneId)
                              )
                        }
                        setCommentsEnabled={setCommentsEnabled}
                        commentsEnabled={commentsEnabled}
                      />
                      <Box ref={sceneRef}>
                        <BoxInner $showSidebar={showSidebar}>
                          <Left $showSidebar={showSidebar}>
                            <ProductionScriptBlockInfo
                              scene={activeScene}
                              sceneNumber={activeSceneNumber}
                              sidebarData={sidebarData}
                              setFirstSceneAsActive={setFirstSceneAsActive}
                            />
                          </Left>
                          <Right>
                            <ProductionWysiwyg
                              scene={activeScene}
                              setShowSidebar={setShowSidebar}
                              commentsEnabled={commentsEnabled}
                              setCommentsEnabled={setCommentsEnabled}
                            />
                          </Right>
                        </BoxInner>
                      </Box>
                    </FormProvider>
                  )}
                </>
              )}
            </>
          ) : (
            <ScriptCreationButtons />
          )}
        </>
      )}
    </>
  );
};

export default ProductionScriptBlock;
